import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { EpisodeStage } from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";

const EpisodeDTOSchema = z.object({
  id: z.string().uuid(),
  patientId: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  stage: z.nativeEnum(EpisodeStage),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type EpisodeDTO = z.infer<typeof EpisodeDTOSchema>;

const EpisodesResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(EpisodeDTOSchema),
});

const EpisodeResponseSchema = z.object({
  success: z.boolean(),
  data: EpisodeDTOSchema,
});

export function useEpisodes(patientId?: string) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["episodes", patientId],
    queryFn: async () => {
      const json = await backend({ method: "GET", url: `/api/v1/episodes/patients/${patientId}/episodes` });
      return EpisodesResponseSchema.parse(json).data;
    },
    enabled: !!patientId,
  });
}

export function useCreateEpisode() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { patientId: string; title: string; description?: string; stage?: EpisodeStage }) => {
      const json = await backend({ method: "POST", url: "/api/v1/episodes/", data: body });
      return EpisodeResponseSchema.parse(json).data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["episodes", data.patientId] });
    },
  });
}

export function useUpdateEpisode() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; title?: string; description?: string | null; stage?: EpisodeStage }) => {
      const json = await backend({ method: "PATCH", url: `/api/v1/episodes/${id}`, data: body });
      return EpisodeResponseSchema.parse(json).data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["episodes", data.patientId] });
    },
  });
}

export function useDeleteEpisode() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patientId }: { id: string; patientId: string }) => {
      await backend({ method: "DELETE", url: `/api/v1/episodes/${id}` });
      return { patientId };
    },
    onSuccess: ({ patientId }) => {
      void queryClient.invalidateQueries({ queryKey: ["episodes", patientId] });
    },
  });
}

export function useAssignEpisode() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ consultationId, episodeId }: { consultationId: string; episodeId: string | null }) => {
      const json = await backend({ method: "PATCH", url: `/api/v1/episodes/consultations/${consultationId}/episode`, data: { episodeId } });
      return json;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["consultations"] });
    },
  });
}

export function useMergeAnonymousPatient() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ anonymousPatientId, targetPatientId }: { anonymousPatientId: string; targetPatientId: string }) => {
      const json = await backend({ method: "POST", url: `/api/v1/episodes/patients/${anonymousPatientId}/merge`, data: { targetPatientId } });
      return json;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });
}
